/**
 * VoteTiles — shared vote UI used by both the post-round pane and the
 * match-start vote pane. Renders a countdown bar, a player profile strip
 * and a grid of vote tiles. Pure presentational; the parent owns vote
 * state and the onVote callback.
 */
import { useEffect, useState } from 'react';
import type { LobbyView } from '@tt/lobby';
import type { CharacterStats, GameTile } from '../../lib/api';
import { Character3DThumbnail } from '../characters/Character3DThumbnail';
import { GameThumb } from './GameThumb';
import { gameAvailability, livePlayerCount } from './gameAvailability';

export interface VoteTilesProps {
  vote: { gameIds: string[]; endsAt: number; tally: Record<string, number> };
  games: GameTile[];
  myCharId: string;
  lobby: LobbyView;
  statsByChar: Record<string, CharacterStats>;
  onVote: (id: string) => void;
  onInspectPlayer?: ((charId: string) => void) | undefined;
  /** When false, PlayerStatsStrip is omitted. PostRoundPane passes false because
   *  the standings strip already covers player identity. Default true. */
  showPlayerStrip?: boolean;
}

export function VoteTiles({ vote, games, myCharId, lobby, statsByChar, onVote, onInspectPlayer, showPlayerStrip = true }: VoteTilesProps) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.ceil((vote.endsAt - now) / 1000));
  const totalSec = Math.max(remaining, Math.ceil((vote.endsAt - (now - 30_000)) / 1000), 30);
  const burnPct = Math.max(0, Math.min(100, (remaining / Math.max(totalSec, 1)) * 100));
  return (
    <div className="vote-section">
      <div className="vote-bar" aria-label={`${remaining} seconds to vote`}>
        <span className="vote-bar-label" aria-live="polite">{remaining}s to vote</span>
        <span className="vote-bar-track" aria-hidden="true">
          <span className="vote-bar-fill" style={{ width: `${burnPct}%` }} />
        </span>
      </div>
      {showPlayerStrip && (
        <PlayerStatsStrip lobby={lobby} games={games} statsByChar={statsByChar} onInspectPlayer={onInspectPlayer} />
      )}
      <div className="vote-grid vote-grid-tall">
        {vote.gameIds.filter((gid) => {
          const tile = games.find((g) => g.id === gid);
          return tile ? gameAvailability(tile, livePlayerCount(lobby)).playable : false;
        }).map((gid, i) => {
          const tile = games.find((g) => g.id === gid);
          const tally = vote.tally[gid] ?? 0;
          return (
            <button
              key={gid}
              type="button"
              className="vote-tile vote-tile-rich"
              data-cat={tile?.category ?? 'reflex'}
              style={{ animationDelay: `${i * 80}ms` }}
              onClick={() => onVote(gid)}
              aria-label={`vote for ${tile?.name ?? gid}`}
            >
              <GameThumb
                gameId={gid}
                category={tile?.category ?? 'reflex'}
                name={tile?.name ?? gid}
                size={84}
                chaoticness={tile?.chaoticness ?? 0}
              />
              <div className="vote-tile-name">{tile?.name ?? gid}</div>
              <div className="vote-tile-pitch">{tile?.pitch ?? ''}</div>
              <div className="vote-tile-meta">
                <span className="meta-pill">{(tile?.durationSec ?? 30)}s</span>
                <span className="meta-pill">{tile?.category ?? '—'}</span>
                {tile?.chaoticness && <span className="meta-pill">{'⚡'.repeat(Math.min(5, tile.chaoticness))}</span>}
              </div>
              {tally > 0 && <div className="vote-tally">{tally}</div>}
            </button>
          );
        })}
      </div>
      <p className="muted vote-hint">Tap your pick. Plurality wins.</p>
      <small style={{ display: 'none' }}>{myCharId}</small>
    </div>
  );
}

function PlayerStatsStrip({ lobby, games, statsByChar, onInspectPlayer }: {
  lobby: LobbyView;
  games: GameTile[];
  statsByChar: Record<string, CharacterStats>;
  onInspectPlayer: ((charId: string) => void) | undefined;
}) {
  return (
    <div className="vote-player-strip" aria-label="player profiles">
      {lobby.players.map((p) => {
        const stats = statsByChar[p.charId];
        const favId = stats?.favoriteGameId ?? mostPlayedGameId(stats);
        const favorite = favId ? findGameTile(games, favId)?.name ?? favId : null;
        const caption = stats
          ? (favorite ? `${stats.roundWins} wins - fav ${favorite}` : `${stats.roundWins} wins`)
          : `${p.lobbyScore} pts this set`;
        return (
          <button key={p.charId} type="button" className="vote-player-chip" onClick={() => onInspectPlayer?.(p.charId)}>
            <Character3DThumbnail icon={p.icon} size={32} frame="portrait" />
            <span>
              <strong>{p.name}</strong>
              <small>{caption}</small>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function mostPlayedGameId(stats?: CharacterStats): string | null {
  if (!stats) return null;
  return Object.entries(stats.perGame).sort((a, b) => b[1].matchesPlayed - a[1].matchesPlayed)[0]?.[0] ?? null;
}

function findGameTile(games: GameTile[], gameId: string): GameTile | undefined {
  return games.find((g) => g.id === gameId) ?? games.find((g) => g.baseGameId === gameId);
}
